import { Button, ButtonGroup, DisplayCard, DisplayCardContainer, Divider, Pullout } from '@aon/aon-tws-common-ui';
import { PulloutType } from '@aon/aon-tws-common-ui/build/lib/components/Pullout';
import {
	HierarchySearch,
	IHierarchyNodeActions,
	IHierarchySearchActionCreators,
	IHierarchyVm,
	SelectHierarchy as Hierarchy,
} from '@aon/tws-common-ui-hierarchy';
import * as React from 'react';
import HtmlParser from 'html-react-parser';
import styled from 'styled-components';
import ContentConstants from '../../../../contentConstants';
import { AudienceSourceType, IAudienceSourceVm, IDistributionHierarchy } from '../../../../models';
import { ActionCreators, AudienceDetailsState } from '../../../../store/AudienceDetailsStore';
import { ActionCreators as CommonHierarchyActionCreators, TCommonHierarchyState } from '../../../../store/CommonHierarchyStore';
import {
	ActionCreators as DistributionHierarchyActionCreators,
	IDistributionHierarchyState,
} from '../../../../store/DistributionHierarchyStore';
import { ActionCreators as HierarchyActionCreators } from '../../../../store/ExternalHierarchyStore';
import { AudienceMode } from '../../../audiences/AudienceMode';
import { CardLayout, Col3Col9, LabelAbove, SaveButtonsContainer, Section, WarningModal } from '../../../common';

const OptionsContainer = styled.div`
	text-align: center;
	margin-bottom: 30px;
`;
export type GroupBasedLinksParams = { audienceSource?: IAudienceSourceVm; mode: AudienceMode } & typeof ActionCreators &
	AudienceDetailsState &
	typeof DistributionHierarchyActionCreators &
	IDistributionHierarchyState &
	typeof HierarchyActionCreators &
	IHierarchyNodeActions &
	IHierarchySearchActionCreators &
	typeof CommonHierarchyActionCreators & { hierarchy: TCommonHierarchyState };
export class GroupBasedLinks extends React.Component<GroupBasedLinksParams, {}> {
	constructor(props: GroupBasedLinksParams) {
		super(props);

		this.goPreviousStep = this.goPreviousStep.bind(this);
		this.closeUnsavedWarningModal = this.closeUnsavedWarningModal.bind(this);
		this.discardUnsavedChangesAndGotoList = this.discardUnsavedChangesAndGotoList.bind(this);
	}

	public componentDidMount() {
		const { audience, audienceSource, GetHierarchyVm, GetDistributionHierarchy } = this.props;

		if (audienceSource && audienceSource.externalHierarchyId) {
			GetHierarchyVm(audienceSource.externalHierarchyId, false, AudienceSourceType.Hierarchy);
		}

		if (audience && audienceSource?.externalHrisId !== undefined) {
			GetDistributionHierarchy(audience.surveyId, audience.audienceId);
		}
	}

	public render() {
		const { hierarchy, mode } = this.props;
		const initializingHierarchy = !!hierarchy.isLoading && !hierarchy.hierarchyHeader;

		return (
			<CardLayout title={ContentConstants.DISTRIBUTIONOPTIONS_PAGE_TITLE} loading={initializingHierarchy}>
				<Section sectionHeader={ContentConstants.SECTIONHEADERS_DISTRIBUTION_OPTIONS}>
					<OptionsContainer className='tws-distribution-options'>
						<DisplayCardContainer>
							<DisplayCard
								headlineSize='sm'
								selected={true}
								headline={ContentConstants.GROUP_BASED_LINKS_USER_HEADLINE}
								descText={ContentConstants.GROUP_BASED_LINKS_USER_DESCRIPTION}
								showArrow={true}
							/>
						</DisplayCardContainer>
					</OptionsContainer>
					<Divider type='dashed' />
					<Col3Col9 label='Instructions' labelSize='lg' clearBefore='40px' className='tws-distribution-instructions'>
						<LabelAbove type='input' textIsHtml={true} text={ContentConstants.INSTRUCTIONALTEXT_GROUP_BASED_LINKS} />
					</Col3Col9>
					<Divider type='dashed' />
					<Col3Col9 label='Distribution List' labelSize='lg' clearBefore='40px' className='tws-distribution-hierarchy-container'>
						<LabelAbove type='input' textIsHtml={true} text={ContentConstants.INSTRUCTIONALTEXT_GROUP_BASED_LINKS_DISTRIBUTION_LIST} />

						<Pullout type={PulloutType.Note}>
							<strong>{ContentConstants.NOTE}</strong>
							{ContentConstants.GROUP_BASED_LINKS_DISTRIBUTION_NOTE_CONTENT}
							<strong>{ContentConstants.SURVEY_ADMIN_HEADER_CONTROL_PANEL}</strong>
							{'.'}
						</Pullout>
					</Col3Col9>
					{!hierarchy.isLoading && hierarchy.couldNotFetchData ? (
						<Pullout type={PulloutType.Warning}>{HtmlParser(ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING)}</Pullout>
					) : (
						<Col3Col9 clearBefore='20px' className='tws-distribution-hierarchy-search-container'>
							{this.HierarchyView(hierarchy)}
						</Col3Col9>
					)}
				</Section>
				<Divider type='dashed' />
				<SaveButtonsContainer style={{ textAlign: 'center' }}>
					<ButtonGroup type='column' reversed={false}>
						<Button id='PreviousStepButton' color='secondary' size='lg' onClick={this.goPreviousStep}>
							{ContentConstants.BUTTONS_PREVIOUS_STEP}
						</Button>
						<Button
							id='SaveButton'
							color='secondary'
							disabled={this.props.distributionHierarchySaveInProgress || this.props.saveInProgress}
							onClick={() => this.props.SaveAudienceWithHierarchy(mode, false)}
							size='lg'
						>
							{ContentConstants.BUTTONS_SAVE}
						</Button>
						<Button
							id='SaveAndCompleteButton'
							color='primary'
							size='lg'
							disabled={this.props.distributionHierarchySaveInProgress || this.props.saveInProgress}
							onClick={() => this.props.SaveAudienceWithHierarchy(mode, true)}
						>
							{mode === AudienceMode.Edit ? ContentConstants.BUTTONS_SAVE_AND_COMPLETE : ContentConstants.BUTTONS_SAVE_AND_PUBLISH}
						</Button>
					</ButtonGroup>
				</SaveButtonsContainer>
				{this.props.unsavedChangesWarningModalOpen && (
					<WarningModal
						isOpen={true}
						contentHeadline={ContentConstants.WARNING_MODAL_UNSAVED_HEADLINE}
						contentBody={ContentConstants.WARNING_MODAL_UNSAVED_BODY}
						onSubmit={this.discardUnsavedChangesAndGotoList}
						onClose={this.closeUnsavedWarningModal}
					/>
				)}
			</CardLayout>
		);
	}

	public componentWillUnmount() {
		this.props.InitState();
		this.props.ResetHierarchyState();
	}

	public handleOnSave(advance: boolean, mode: AudienceMode) {
		this.props.SaveAudienceWithHierarchy(mode, advance);
	}

	public goPreviousStep() {
		const { audience, mode } = this.props;
		this.props.GoToPreviousStep(
			audience,
			mode,
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			this.getUnsavedDistributionHierarchy(),
		);
	}

	public discardUnsavedChangesAndGotoList() {
		const { audience, mode } = this.props;
		this.props.ToggleUnsavedWarningModal(false);
		this.props.GoToAudienceDetails(audience.surveyId, mode, audience.audienceId);
	}

	public closeUnsavedWarningModal() {
		this.props.ToggleUnsavedWarningModal(false);
	}

	private HierarchyView(hierarchy: IHierarchyVm) {
		return (
			<>
				<HierarchySearch {...this.props} {...hierarchy.hierarchySearchState} />
				<Hierarchy
					{...this.props}
					{...hierarchy}
					onChangeSelectionCriteria={() => {
						/* */
					}}
					selectAll={true}
					disabled={true}
					disableSelectAll={true}
					hideselectItemRadioButtons={true}
					hideClearButton={true}
				/>
			</>
		);
	}

	private getUnsavedDistributionHierarchy() {
		const { audience, distributionHierarchy, audienceSource, selectionCriteria } = this.props;

		const unsavedDistributionHierarchy: IDistributionHierarchy = {
			distributionHierarchyId: distributionHierarchy.distributionHierarchyId,
			audienceId: audience.audienceId,
			externalHrisId: audienceSource?.externalHrisId || '',
			externalHierarchyId: audienceSource?.externalHierarchyId,
			surveyHierarchyId: distributionHierarchy.surveyHierarchyId,
			hierarchySelection: JSON.stringify(selectionCriteria),
		};

		return unsavedDistributionHierarchy;
	}
}
